import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import TitleBarComponent from "../../../components/titlebar/TitleBarComponent";
import { DatePicker, Form, Select, Spin, Empty } from "antd";
import { IHistory } from "../../../models/history";
import { getHistoryApi } from "../../../apis/histories";

dayjs.extend(utc);
const { RangePicker } = DatePicker;

const HistoriesPage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(false);
  const [entity, setEntity] = useState("seat");
  const [history, listHistory] = useState<IHistory[]>([]);
  const [value, setValue] = useState<[Dayjs, Dayjs]>(() => [
    dayjs().startOf("day"), // 00:00 hôm nay
    dayjs(), // thời điểm hiện tại
  ]);

  // ----- CALL API -----
  const handleFetchHistories = async (
    from: Dayjs,
    to: Dayjs,
    entityType: string
  ) => {
    try {
      setLoading(true);
      const res = await getHistoryApi(
        from.utc().toISOString(),
        to.utc().toISOString(),
        entityType
      );
      listHistory(res?.data ?? []);
    } catch (error) {
      console.error("Fetch history error:", error);
      listHistory([]);
    } finally {
      setLoading(false);
    }
  };

  // ----- Khi đổi DateRange -----
  const handleDateChange = (dates: null | (Dayjs | null)[]) => {
    if (!dates || !dates[0] || !dates[1]) return;

    const from = dates[0];
    const to = dates[1];

    setValue([from, to]);
    handleFetchHistories(from, to, entity);
  };

  // ----- Khi đổi entity -----
  const handleEntityChange = (val: string) => {
    setEntity(val);
    handleFetchHistories(value[0], value[1], val);
  };

  // Gọi API lần đầu khi mở trang
  useEffect(() => {
    handleFetchHistories(value[0], value[1], entity);
  }, []);

  return (
    <div className="pb-10">
      <TitleBarComponent title="Lịch sử" />

      {/* Filters */}
      <div className="mt-3 w-full px-2">
        <div className="flex flex-col gap-3 w-full md:flex-row md:items-center md:justify-end">
          {/* Date range */}
          <div className="w-full md:w-auto">
            <div className="text-sm font-medium mb-1">Thời gian</div>
            <RangePicker
              className="w-full"
              value={value}
              onChange={handleDateChange}
              showTime={{ format: "HH:mm" }}
              format="DD-MM-YYYY HH:mm"
              allowClear={false}
              size="large"
            />
          </div>

          {/* Entity filter */}
          <div className="w-full md:w-[180px]">
            <div className="text-sm font-medium mb-1">Lọc theo</div>
            <Select
              className="w-full"
              size="large"
              value={entity}
              onChange={handleEntityChange}
              options={[
                { value: "account", label: "Tài khoản" },
                { value: "seat", label: "Ghế" },
                { value: "trip", label: "Chuyến đi" },
                { value: "bus", label: "Xe" },
                { value: "customer", label: "Khách hàng" },
              ]}
            />
          </div>
        </div>
      </div>

      {/* History list */}
      <div className="mt-4 px-2">
        {/* Loading overlay */}
        {loading && (
          <div className="flex justify-center py-10">
            <Spin size="large" />
          </div>
        )}

        {/* Empty */}
        {!loading && history.length === 0 && (
          <div className="py-10 flex justify-center">
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="Không có lịch sử"
            />
          </div>
        )}

        {/* List */}
        {!loading && history.length > 0 && (
          <div className="flex flex-col gap-3 pb-5">
            {history.map((his, index) => (
              <div
                key={index}
                className="
                  p-3 rounded-xl shadow-md bg-white border
                  active:scale-[0.97] active:bg-gray-100
                  transition-all duration-75
                "
              >
                <p className="font-semibold text-gray-900">{his.description}</p>

                {/* Nếu API có thêm detail/time, bạn có thể thêm dưới đây */}
                {his?.createdAt && (
                  <p className="text-sm text-gray-500 mt-1">
                    {dayjs(his.createdAt).format("DD/MM/YYYY HH:mm")}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default HistoriesPage;
